import { ApiResponse, ApiError } from '@voyager/types';

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: string;
  headers?: Record<string, string>;
}

class ConsumerApiClient {
  private baseUrl: string;

  constructor() {
    // Use environment variable or fallback to localhost for development
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    console.log('🌐 Consumer API Client initialized with baseURL:', this.baseUrl);
  }

  /**
   * Make HTTP request to Creator app API
   */
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}/api${endpoint}`;
    const { method = 'GET', body, headers = {} } = config;

    try {
      console.log(`🌐 ${method} ${url}`);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...headers,
        },
        body,
        cache: 'no-store',
      });

      // Handle non-JSON responses (like 404 HTML pages)
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        return {
          success: false,
          error: {
            code: 'NON_JSON_RESPONSE',
            message: `Expected JSON response but got ${contentType}. Status: ${response.status}`,
            details: { status: response.status, statusText: response.statusText }
          }
        };
      }

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || {
            code: 'HTTP_ERROR',
            message: data.message || `HTTP ${response.status}: ${response.statusText}`
          }
        };
      }

      return {
        success: true,
        data,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('🚨 API Request failed:', error);
      
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed'
        }
      };
    }
  }

  /**
   * Health check - test if API is available
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.request('/health');
      return response.success;
    } catch {
      return false;
    }
  }

  /**
   * Create standardized error from API error response
   */
  createStandardError(apiError: ApiError) {
    return {
      type: this.mapErrorType(apiError.code),
      message: apiError.message,
      code: apiError.code,
      details: apiError.details,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Map API error codes to standardized error types
   */
  private mapErrorType(code?: string) {
    if (!code) return 'UNKNOWN_ERROR';
    
    const errorTypeMap: Record<string, string> = {
      'VALIDATION_ERROR': 'VALIDATION_ERROR',
      'AUTHENTICATION_ERROR': 'AUTHENTICATION_ERROR', 
      'AUTHORIZATION_ERROR': 'AUTHORIZATION_ERROR',
      'NOT_FOUND_ERROR': 'NOT_FOUND_ERROR',
      'CONFLICT_ERROR': 'CONFLICT_ERROR',
      'SERVER_ERROR': 'SERVER_ERROR',
      'NETWORK_ERROR': 'NETWORK_ERROR',
      'HTTP_ERROR': 'SERVER_ERROR',
      'NON_JSON_RESPONSE': 'SERVER_ERROR'
    };

    return errorTypeMap[code] || 'UNKNOWN_ERROR';
  }
}

// Export singleton instance
export const consumerApiClient = new ConsumerApiClient();